import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { ContentPicker } from '../library/picker/ContentPicker'
import type { VaultItem } from '../library/vault/vaultData'
import { AmountDialog, ExpiryDialog, LabelsPicker } from './home/Pickers'
import { PRICE_MIN, type Label } from './home/homeData'
import { expireDaysBetween, previewIdsForPaid, type EditPostPatch } from './editPost'
import { htmlToText, textToHtml } from './ofText'
import type { OfApi } from './ofApi'
import type { Post } from './posts'

type Sub = null | 'labels' | 'price' | 'expiry' | 'media'

/**
 * Full post edit (text + media + labels + price + expiry) for the feed and the queue.
 * Sub-panels mutate a local draft; Save sends one PUT.
 */
export function EditPostSheet({
  post,
  accountId,
  of,
  busy,
  expireFrom,
  onClose,
  onSave,
}: {
  post: Post
  accountId: string
  of: OfApi
  busy: boolean
  /** Anchor for the current expiry chip (publish time in the queue, postedAt on the feed). */
  expireFrom?: string | null
  onClose: () => void
  onSave: (patch: EditPostPatch) => void
}) {
  const { t } = useTranslation('ofPages')
  const [text, setText] = useState(() => htmlToText(post.text))
  const [labels, setLabels] = useState<Label[]>(() => post.labels.map((l) => ({ id: Number(l.id), name: l.name, posts: 0, previews: [] })))
  const [price, setPrice] = useState(post.price)
  const [media, setMedia] = useState<VaultItem[]>(post.media)
  const [previewIds, setPreviewIds] = useState<number[]>(() => previewIdsForPaid(post.media, post.price, post.previewIds))
  const [expireDays, setExpireDays] = useState<number | null>(() => expireDaysBetween(expireFrom ?? post.postedAt, post.expiredAt))
  const [expireTouched, setExpireTouched] = useState(false)
  const [sub, setSub] = useState<Sub>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (sub) setSub(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, sub])

  const dirty =
    text.trim() !== htmlToText(post.text).trim() ||
    price !== post.price ||
    media.length !== post.media.length ||
    media.some((m, i) => m.id !== post.media[i]?.id) ||
    labels.length !== post.labels.length ||
    labels.some((l, i) => String(l.id) !== post.labels[i]?.id) ||
    expireTouched

  const save = () => {
    if (!text.trim() || busy || !dirty) return
    const patch: EditPostPatch = {
      textHtml: textToHtml(text),
      labels: labels.map((l) => ({ id: String(l.id), name: l.name })),
      price,
      media,
      previewIds: previewIdsForPaid(media, price, previewIds),
    }
    if (expireTouched) patch.expireDays = expireDays
    onSave(patch)
  }

  return (
    <>
      <div className="ofp-modal-back" onClick={onClose}>
        <div className="ofp-modal" role="dialog" aria-modal="true" aria-label={t('post.editTitle')} onClick={(e) => e.stopPropagation()}>
          <h3 className="ofp-modal-title">{t('post.editTitle')}</h3>
          <textarea className="ofp-textarea" aria-label={t('post.text')} value={text} onChange={(e) => setText(e.target.value)} rows={6} autoFocus />
          <div className="ofp-edit-tools">
            <button type="button" className="lib-btn" onClick={() => setSub('media')}>
              <FeatherIcon name="image" size={14} />
              {media.length ? `${t('post.editMedia')} (${media.length})` : t('post.editMedia')}
            </button>
            <button type="button" className="lib-btn" onClick={() => setSub('labels')}>
              <FeatherIcon name="tag" size={14} />
              {labels.length ? labels.map((l) => l.name).join(' · ') : t('post.labels')}
            </button>
            <button type="button" className="lib-btn" onClick={() => setSub('price')}>
              <FeatherIcon name="dollar-sign" size={14} />
              {price > 0 ? t('post.price', { price: price.toFixed(2) }) : t('post.addPrice')}
            </button>
            <button type="button" className="lib-btn" onClick={() => setSub('expiry')}>
              <FeatherIcon name="clock" size={14} />
              {expireDays ? `${t('post.expires')} (${expireDays}d)` : t('post.setExpiry')}
            </button>
            {expireDays !== null && (
              <button
                type="button"
                className="lib-btn lib-btn--ghost"
                onClick={() => {
                  setExpireDays(null)
                  setExpireTouched(true)
                }}
              >
                {t('post.clearExpiry')}
              </button>
            )}
          </div>
          <p className="ofp-edit-hint">{t('post.editHint', { min: PRICE_MIN })}</p>
          <div className="ofp-modal-actions">
            <button type="button" className="lib-btn lib-btn--ghost" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !text.trim() || !dirty} onClick={save}>
              {t('save')}
            </button>
          </div>
        </div>
      </div>
      {sub === 'labels' && <LabelsPicker of={of} chosen={labels} onChange={setLabels} onClose={() => setSub(null)} />}
      {sub === 'price' && (
        <AmountDialog
          kind="price"
          value={price || null}
          onSave={(v) => {
            const next = v ?? 0
            setPrice(next)
            setPreviewIds(previewIdsForPaid(media, next, previewIds))
            setSub(null)
          }}
          onClose={() => setSub(null)}
        />
      )}
      {sub === 'expiry' && (
        <ExpiryDialog
          value={expireDays}
          onSave={(d) => {
            setExpireDays(d)
            setExpireTouched(true)
            setSub(null)
          }}
          onClose={() => setSub(null)}
        />
      )}
      {sub === 'media' && (
        <ContentPicker
          accountId={accountId}
          initial={media}
          max={40}
          onDone={(next) => {
            setMedia(next)
            setPreviewIds(previewIdsForPaid(next, price, previewIds))
            setSub(null)
          }}
          onClose={() => setSub(null)}
        />
      )}
    </>
  )
}
